// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import CartLinesListHeader, { CartLinesListHeaderStyles } from "@insite/content-library/Components/CartLinesListHeader";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LinesHeader, { LinesHeaderStyles } from "@pace/App/Components/Cart/LinesHeader";
import CartLineCardCondensed, { CartLineCardCondensedStyles } from "@pace/Overrides/Components/CartLineCardCondensed";
import CartLineCardExpanded, { CartLineCardExpandedStyles } from "@pace/Overrides/Components/CartLineCardExpanded";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    isCondensed: boolean;
    hideCondensedSelector?: boolean;
    onChangeIsCondensed?: (event: React.SyntheticEvent<Element, Event>, value: boolean) => void;
    editable: boolean;
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    extendedStyles?: CartLinesListStyles;
    // XNG-Changes
    cartLinesHeaderStyle?: LinesHeaderStyles;
    headerTitles?: string[];
}
export interface CartLinesListStyles {
    wrapper?: InjectableCss;
    header?: CartLinesListHeaderStyles;
    cardCondensed?: CartLineCardCondensedStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    pagination?: PaginationPresentationProps;
    // XNG-Changes
    sectionWrapper?: InjectableCss;
}
export const orderLinesListStyles: CartLinesListStyles = {
    pagination: {
        cssOverrides: {
            pagination: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
};
const CartLinesList: FC<OwnProps> = ({
    cart,
    promotions,
    isCondensed,
    hideCondensedSelector,
    onChangeIsCondensed,
    editable,
    showSavingsAmount,
    showSavingsPercent,
    extendedStyles,
    cartLinesHeaderStyle,
    headerTitles,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));
    if (!cart.cartLines) {
        return null;
    }
    const cartLines =
        cart.status === "Cart" ? cart.cartLines.filter(cartline => !cartline.quoteRequired) : cart.cartLines;
    const CartLineComponent = isCondensed ? CartLineCardCondensed : CartLineCardExpanded;
    const linesHeaderRightTitles = isCondensed ? headerTitles || ["SKU", "Price", "QTY", "Total"] : undefined;
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`cardLinesList_${cart.id}`}>
            <CartLinesListHeader
                productsCount={cartLines.length}
                isCondensed={isCondensed}
                hideCondensedSelector={hideCondensedSelector}
                onChangeIsCondensed={onChangeIsCondensed}
                extendedStyles={styles.header}
            />
            {/* XNG-Changes */}
            <StyledWrapper {...{ isCondensed }} {...styles.sectionWrapper}>
                <LinesHeader
                    isCondensed={isCondensed}
                    rightTitlesForCondensed={linesHeaderRightTitles}
                    rightTitles={headerTitles}
                    extendedStyles={cartLinesHeaderStyle}
                    leftTitle={translate("Product")}
                />
                {cartLines!.map(cartLine => {
                    const cartLinePromotions = promotions?.filter(promotion => promotion.orderLineId === cartLine.id);
                    return (
                        <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                            <CartLineComponent
                                cart={cart}
                                promotions={cartLinePromotions}
                                editable={editable}
                                showSavingsAmount={showSavingsAmount}
                                showSavingsPercent={showSavingsPercent}
                                showInvalidPriceMessage={true}
                                extendedStyles={isCondensed ? styles.cardCondensed : styles.cardExpanded}
                            />
                        </CartLineContext.Provider>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};
export default CartLinesList;
